// src/services/msalInstanceManager.js

/**
 * Centralized MSAL instance manager for consistent authentication across tabs.
 * Simplified to remove popups, notifications, and complex retry logic.
 * Users stay logged in indefinitely until explicit logout.
 */

let isInitialized = false;
let initializationPromise = null;

/**
 * Initialize MSAL instance and handle redirect promise.
 * This should be called ONLY ONCE during app initialization.
 * Subsequent calls will return the cached promise.
 * 
 * @param {PublicClientApplication} instance - MSAL instance
 * @returns {Promise<AuthenticationResult|null>} Redirect response or null
 */
export const initializeMsalInstance = async (instance) => {
  // If already initialized, return cached promise
  if (initializationPromise) {
    console.log('MSAL initialization already in progress, returning cached promise');
    return initializationPromise;
  }

  // If already completed, return null
  if (isInitialized) {
    console.log('MSAL already initialized');
    return null;
  }

  // Create and cache the initialization promise
  initializationPromise = (async () => {
    try {
      console.log('Initializing MSAL instance...');
      
      // Handle redirect promise (returns user info if redirecting back from B2C)
      const response = await instance.handleRedirectPromise();
      
      if (response) {
        console.log('Redirect response received:', response.account?.username);
        // Set the account as active
        instance.setActiveAccount(response.account);
      } else {
        // No redirect response, check if we have accounts
        const accounts = instance.getAllAccounts();
        
        if (accounts.length > 0) {
          // Set first account as active if none is set
          const activeAccount = instance.getActiveAccount();
          if (!activeAccount) {
            console.log('Setting first available account as active:', accounts[0].username);
            instance.setActiveAccount(accounts[0]);
          }
        } else {
          console.log('No accounts found in MSAL cache');
        }
      }
      
      isInitialized = true;
      console.log('MSAL initialization complete');
      return response;
      
    } catch (error) {
      console.error('MSAL initialization error:', error);
      // Reset promise so it can be retried
      initializationPromise = null;
      throw error;
    }
  })();

  return initializationPromise;
};

/**
 * Get the active account from MSAL instance.
 * Ensures an account is set as active if available.
 * 
 * @param {PublicClientApplication} instance - MSAL instance
 * @returns {AccountInfo|null} Active account or null
 */
export const getActiveAccount = (instance) => {
  // Validate that we have a proper MSAL instance
  if (!instance || typeof instance.getActiveAccount !== 'function') {
    console.error('getActiveAccount called with invalid MSAL instance:', instance);
    return null;
  }

  let activeAccount = instance.getActiveAccount();
  
  // If no active account but accounts exist, set the first one
  if (!activeAccount) {
    const accounts = instance.getAllAccounts();
    if (accounts.length > 0) {
      console.log('No active account set, using first available:', accounts[0].username);
      instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }
  }
  
  return activeAccount;
};

/**
 * Check if MSAL has been initialized
 * @returns {boolean}
 */
export const isMsalInitialized = () => isInitialized;

/**
 * Reset initialization state (for logout)
 */
export const resetMsalInitialization = () => {
  isInitialized = false;
  initializationPromise = null;
  console.log('MSAL initialization state reset');
};

/**
 * Acquire token silently - simplified without retry logic or notifications
 * @param {PublicClientApplication} instance - MSAL instance
 * @param {object} request - Token request (scopes, etc.)
 * @returns {Promise<AuthenticationResult>}
 */
export const acquireTokenSilently = async (instance, request) => {
  const account = getActiveAccount(instance);
  
  if (!account) {
    throw new Error('No active account available for token acquisition');
  }

  try {
    console.log('Acquiring token silently...');
    
    const response = await instance.acquireTokenSilent({
      ...request,
      account: account,
      forceRefresh: false // Let MSAL handle caching
    });
    
    console.log('Token acquired successfully');
    return response;
    
  } catch (error) {
    console.error('Silent token acquisition failed:', error);
    
    // If interaction is required, redirect to login
    if (error.name === 'InteractionRequiredAuthError') {
      console.log('Interaction required - redirecting to login');
      await instance.acquireTokenRedirect({
        ...request,
        account: account
      });
      // Redirect will occur, no return
    }
    
    throw error;
  }
};

/**
 * Get token expiry time from account
 * @param {AccountInfo} account - MSAL account
 * @returns {number|null} Expiry time in milliseconds or null
 */
export const getTokenExpiryTime = (account) => {
  if (!account || !account.idTokenClaims?.exp) {
    return null;
  }
  return account.idTokenClaims.exp * 1000; // Convert to milliseconds
};
